/**
 * Shared "Total Annual Spend" calculation, used by both AssetDetailPage.tsx (one asset's own
 * Spend card) and InitiativeDetailPage.tsx (the rollup across every asset linked to a
 * Portfolio/Programme/Project via Linked Artefacts). Kept in one place so the two never drift.
 */

export interface BillingEntryLike {
  date: string;
  currency?: string;
  amount?: number;
}

// Static, approximate rates (how many USD one unit of this currency is worth) - NOT a live
// feed. There's no exchange-rate source anywhere in this app; building one is a real, separate
// piece of infrastructure. Good enough for an internal spend rollup - revisit if this ever
// needs to be audit-grade or genuinely FX-sensitive. USD is the reporting baseline (not GBP -
// changed per explicit request, since spend here is mostly entered in USD).
export const CURRENCY_TO_USD: Record<string, number> = {
  USD: 1,
  GBP: 1.27,
  EUR: 1.09,
  NOK: 0.093,
  SEK: 0.096,
  DKK: 0.146,
  CHF: 1.14,
};

export const convertToUsd = (amount: number, currency?: string): number =>
  amount * (CURRENCY_TO_USD[currency || 'USD'] ?? 1);

// Purchase Cost is a one-off acquisition, deliberately excluded - "annual spend" is what the
// Billing section's recurring entries actually add up to. Only entries dated in the CURRENT
// calendar year count, and only entries that both have a date and an amount - a date-only row
// (billed but not yet costed out) can't contribute to a total.
export const totalAnnualSpendUsd = (billingEntries: BillingEntryLike[] = []): number => {
  const currentYear = new Date().getFullYear();
  return billingEntries
    .filter((b) => b.amount != null && b.date && new Date(b.date).getFullYear() === currentYear)
    .reduce((sum, b) => sum + convertToUsd(b.amount as number, b.currency), 0);
};

export const formatUsd = (value: number): string =>
  `$${value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
